<template>
  <div
    class="slideshow-section memory-book-section container"
    v-if="book || upcoming"
  >
    <div class="slideshow-section__player memory-book-section__player">
      <MemoryBookFlip
        class="memory-book-section__book"
        larger
        strip
        v-if="book"
        ref="flip"
        :autoplay="{ allowed: autoplayAllowed() }"
        :book="book"
        :key-order="KEY_ORDER.section"
        :section-id="sectionId"
        :single="phone"
        :size="phone ? 'small' : 'normal'"
        @expand="openViewer"
      />
      <div class="memory-book-section__scene" v-else-if="!book">
        <BookScene
          :name="cover.name"
          :portrait="cover.portrait"
          :years="cover.years"
          :zoom="1.15"
        />
      </div>
    </div>

    <div class="slideshow-section__side memory-book-section__side">
      <small class="slideshow-section__eyebrow" v-text="texts.chip" />
      <h2 class="slideshow-section__name">
        {{ sprintf(texts.heading, name) }}
      </h2>
      <template v-if="book">
        <p v-text="texts.text" />
        <span class="memory-book-section__status" v-text="status" />
        <div class="memory-book-section__actions">
          <button
            class="btn obituary"
            type="button"
            @click="openBookPanel('section')"
            v-text="texts.title"
          />
        </div>
      </template>
      <template v-else-if="upcoming">
        <p v-text="texts.upcomingText" />
        <span class="memory-book-section__status" v-text="upcomingStatus" />
        <div class="memory-book-section__actions">
          <button
            class="btn obituary"
            type="button"
            @click="addPhoto"
            v-text="addPhotoText"
          />
        </div>
      </template>
    </div>

    <MemoryBookViewer
      v-if="book && viewing"
      :book="book"
      :label="sprintf(texts.heading, name)"
      :playing="viewing.playing"
      :section-id="sectionId"
      :spread="viewing.spread"
      @close="closeViewer"
    />
  </div>
</template>

<script lang="ts" setup>
  import { sprintf } from "sprintf-js";
  import {
    computed,
    nextTick,
    onBeforeUnmount,
    onMounted,
    ref,
    watch,
  } from "vue";
  import { useStore } from "vuex";
  import { quickContent, useMediaQuery } from "../../composables/useSlideshow";
  import { MEDIA_INITIAL } from "../../createCommentStepsHelper";
  import { autoplayAllowed, watchSeen } from "../../highlights/book-autoplay";
  import { KEY_ORDER } from "../../highlights/book-keys";
  import {
    formatPrice,
    lowestPrice,
    saleWhen,
  } from "../../highlights/memory-book";
  import {
    bookPanel,
    loadMemoryBook,
    openBookPanel,
  } from "../../highlights/memory-book-panel";
  import { highlightTexts, memoryBookTexts } from "../../highlights/texts";
  import { trackBook } from "../../slideshow/track";
  import BookScene from "./BookScene.vue";
  import MemoryBookFlip from "./MemoryBookFlip.vue";
  import MemoryBookViewer from "./MemoryBookViewer.vue";

  /**
   * The memory book's own section, after Bildspel (PRO-4397). It is there only when the API sells
   * the book here or will (`book` or `upcoming`); the page renders it hidden and shows it then,
   * and the quick menu gets its entry.
   */
  const props = defineProps<{
    name: string;
    postId: string;
    sectionId: string;
    /** IANA zone of the ceremony's calendar days, or '' for the visitor's. */
    timeZone: string;
  }>();

  /** $breakpoint-s: below it the book shows one page at a time. */
  const PHONE = "(max-width: 767px)";
  /** Arriving by link: the book opens once it is in view, or after this long anyway. */
  const OPEN_FALLBACK_MS = 4000;
  /** Greetings above render after the book is known and push it down: aim again for this long. */
  const SETTLE_MS = 4000;
  const SETTLE_STEP_MS = 250;
  /** The reader scrolls or reaches for the page (the scrollbar too): stop aiming. */
  const STOP_EVENTS = ["wheel", "touchstart", "keydown", "pointerdown"];
  /** About as long as the browser's smooth scroll takes, even over a long page. */
  const SMOOTH_MS = 1200;
  /** "Bläddra i boken": the next spread after … */
  const BROWSE_STEP_MS = 3800;

  const store = useStore();
  const texts = memoryBookTexts();
  /** The same ask as the book's card in Aktuellt. */
  const addPhotoText = highlightTexts().photosCta ?? "";
  const phone = useMediaQuery(PHONE);
  const flip = ref<InstanceType<typeof MemoryBookFlip> | null>(null);
  const viewing = ref<{ playing: boolean; spread: number } | null>(null);

  const book = computed(() => bookPanel.book);
  const upcoming = computed(() => (bookPanel.book ? null : bookPanel.upcoming));

  // The printed cover names them without the nickname.
  const content = quickContent();
  const cover = {
    name: content?.deceased.formalName || content?.deceased.name || props.name,
    portrait: content?.deceased.portrait ?? null,
    years: content?.deceased.years ?? "",
  };

  const status = computed(() => {
    const b = book.value;
    if (!b) {
      return "";
    }
    return sprintf(
      b.shipping ? texts.statusShipping : texts.status,
      b.pages,
      formatPrice(lowestPrice(b), b.currency)
    );
  });

  const upcomingStatus = computed(() => {
    const shareAt = upcoming.value?.shareAt;
    return shareAt
      ? saleWhen(shareAt, texts, props.timeZone)
      : texts.upcomingLater;
  });

  function addPhoto() {
    store.dispatch("createComment/toggleModal", MEDIA_INITIAL);
  }

  /** On the same spread; if the section was turning by itself, the large view carries on. */
  function openViewer(spread: number) {
    viewing.value = { playing: !!flip.value?.playing, spread };
    flip.value?.stop();
    trackBook({ action: "larger" });
  }

  /** The section stands on the spread the reader left the large view on, without a turn. */
  function closeViewer(spread: number) {
    flip.value?.toSpread(spread);
    viewing.value = null;
  }

  /**
   * To the section, then the cover opens with a turn once the book is in view. From Aktuellt it
   * also keeps turning by itself.
   */
  let unwatchArrival: (() => void) | null = null;
  let stopSettling: (() => void) | null = null;

  /** Where the section starts under the sticky quick menu. */
  function targetOf(host: HTMLElement): number {
    const menu = document.querySelector(".single-obituary-navigation__wrapper");
    const below = menu?.getBoundingClientRect().height ?? 0;
    const target =
      host.getBoundingClientRect().top + window.scrollY - below + 2;
    // Near the end of the page the window stops short of it.
    const end = document.documentElement.scrollHeight - window.innerHeight;
    return Math.max(0, Math.min(target, end));
  }

  /**
   * Scrolls to the section and keeps it there while the page above is still filling in, until it
   * holds still or the reader scrolls. The address gets the section's hash without a new entry.
   */
  function scrollToSection(host: HTMLElement) {
    stopSettling?.();
    const smooth = !window.matchMedia("(prefers-reduced-motion: reduce)")
      .matches;
    let target = targetOf(host);
    window.scrollTo({ behavior: smooth ? "smooth" : "auto", top: target });
    history.replaceState(history.state, "", `#${props.sectionId}`);
    const until = performance.now() + SETTLE_MS;
    // After the first smooth scroll, the window itself must stand there too: the browser's
    // scroll anchoring can move it while the page above fills in, the target unchanged.
    const smoothUntil = performance.now() + (smooth ? SMOOTH_MS : 0);
    let timer = 0;
    const halt = () => {
      window.clearTimeout(timer);
      for (const type of STOP_EVENTS) {
        window.removeEventListener(type, halt);
      }
      stopSettling = null;
    };
    const settle = () => {
      const now = targetOf(host);
      const off =
        performance.now() > smoothUntil && Math.abs(window.scrollY - now) > 4;
      if (Math.abs(now - target) > 4 || off) {
        target = now;
        window.scrollTo({ behavior: "auto", top: target });
      }
      if (performance.now() < until) {
        timer = window.setTimeout(settle, SETTLE_STEP_MS);
      } else {
        halt();
      }
    };
    for (const type of STOP_EVENTS) {
      window.addEventListener(type, halt, { once: true, passive: true });
    }
    timer = window.setTimeout(settle, SETTLE_STEP_MS);
    stopSettling = halt;
  }

  async function arrive(browse: boolean) {
    await nextTick();
    const host = document.getElementById(props.sectionId);
    if (!(host && book.value)) {
      return;
    }
    scrollToSection(host);
    unwatchArrival?.();
    const stage = host.querySelector(".book-flip__stage");
    let done = false;
    const open = () => {
      if (done) {
        return;
      }
      done = true;
      unwatchArrival?.();
      unwatchArrival = null;
      flip.value?.open();
      if (browse) {
        flip.value?.play(BROWSE_STEP_MS);
      }
    };
    const fallback = window.setTimeout(open, OPEN_FALLBACK_MS);
    const stop = stage ? watchSeen(stage, (seen) => seen && open()) : null;
    unwatchArrival = () => {
      window.clearTimeout(fallback);
      stop?.();
    };
  }

  /** `#minnesbok`, and `?minnesbok=1` from the visitors' mail (it landed at the top before). */
  function linkedHere(): boolean {
    const params = new URLSearchParams(window.location.search);
    return (
      window.location.hash === `#${props.sectionId}` ||
      params.get("minnesbok") === "1"
    );
  }

  function onHashChange() {
    if (window.location.hash === `#${props.sectionId}`) {
      arrive(false);
    }
  }

  // The section and its quick menu entry appear with the book.
  let shown = false;
  watch(
    () => !!(bookPanel.book || bookPanel.upcoming),
    async (has) => {
      bookPanel.section = !!bookPanel.book;
      bookPanel.sectionId = props.sectionId;
      const host = document.getElementById(props.sectionId);
      if (!(has && host) || shown) {
        return;
      }
      shown = true;
      host.hidden = false;
      store.commit("sections/addSection", host);
      if (book.value && linkedHere()) {
        await arrive(false);
      }
    },
    { immediate: true }
  );

  watch(
    () => bookPanel.browse,
    () => arrive(true)
  );

  onMounted(() => {
    if (!(bookPanel.book || bookPanel.upcoming)) {
      loadMemoryBook(props.postId);
    }
    window.addEventListener("hashchange", onHashChange);
  });

  onBeforeUnmount(() => {
    unwatchArrival?.();
    stopSettling?.();
    window.removeEventListener("hashchange", onHashChange);
  });
</script>
